import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, DollarSign, User, CheckCircle, Clock, AlertCircle,
  Printer, ArrowLeft, CreditCard, Building
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { formatDate } from '../../utils/dateTimeUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PayCautionMoney() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentStatus, setStudentStatus] = useState(null);
  const [banks, setBanks] = useState([]);
  const [collegeInfo, setCollegeInfo] = useState(null);
  const [paymentData, setPaymentData] = useState({
    amount: '',
    payment_mode: 'Cash',
    bank_id: '',
    bank_name: '',
    transaction_ref: '',
    remarks: '',
    payment_date: new Date().toISOString().split('T')[0]
  });
  const [receipt, setReceipt] = useState(null);

  useEffect(() => {
    fetchBanks();
    loadCollegeInfo();
  }, []);

  const loadCollegeInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/tenants/my-college/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCollegeInfo({
          ...data,
          college_name: data.name || data.college_name,
          logo_url: data.logo || data.logo_url
        });
      }
    } catch (error) {
      console.error('Error fetching college info:', error);
    }
  };

  const fetchBanks = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/bank-register/banks`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBanks(data || []);
      }
    } catch (error) {
      console.error('Error fetching banks:', error);
    }
  };

  const searchStudents = async () => {
    if (!searchQuery.trim()) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/students/?search=${encodeURIComponent(searchQuery)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStudents(data.slice(0, 10));
      }
    } catch (error) {
      console.error('Search error:', error);
    }
  };

  const selectStudent = async (student) => {
    setSelectedStudent(student);
    setStudents([]);
    setSearchQuery('');
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/caution-money/student/${student.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStudentStatus(data);
        setPaymentData(prev => ({
          ...prev,
          amount: data.required_amount - data.total_paid > 0 ? data.required_amount - data.total_paid : ''
        }));
      }
    } catch (error) {
      console.error('Error fetching status:', error);
    }
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    
    if (!selectedStudent || !paymentData.amount) {
      toast.error('Please select student and enter amount');
      return;
    }

    if (paymentData.payment_mode !== 'Cash' && !paymentData.bank_id) {
      toast.error('Please select bank for non-cash payment');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/caution-money/pay`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          student_id: selectedStudent.id,
          amount: parseFloat(paymentData.amount),
          payment_mode: paymentData.payment_mode,
          bank_id: paymentData.bank_id || null,
          bank_name: paymentData.bank_name || null,
          transaction_ref: paymentData.transaction_ref || null,
          remarks: paymentData.remarks || null,
          payment_date: paymentData.payment_date || null
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success('Caution money collected successfully!');
        setReceipt({
          ...data,
          student: selectedStudent,
          amount: parseFloat(paymentData.amount),
          payment_mode: paymentData.payment_mode,
          date: formatDate(),
          tenant: data.tenant || collegeInfo || {}
        });
      } else {
        toast.error(data.detail || 'Payment failed');
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Failed to process payment');
    } finally {
      setLoading(false);
    }
  };

  const printReceipt = () => {
    window.print();
  };

  const resetForm = () => {
    setSelectedStudent(null);
    setStudentStatus(null);
    setReceipt(null);
    setPaymentData({
      amount: '',
      payment_mode: 'Cash',
      bank_id: '',
      bank_name: '',
      transaction_ref: '',
      remarks: ''
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Pay Caution Money</h1>
          <p className="text-gray-600 mt-1">Collect caution money (security deposit) from students</p>
        </div>
      </div>

      {!receipt ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left - Search & Select Student */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Select Student
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Search */}
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && searchStudents()}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="Search by name or admission number..."
                  />
                </div>
                <Button onClick={searchStudents}>Search</Button>
              </div>

              {/* Search Results */}
              {students.length > 0 && (
                <div className="border rounded-lg divide-y max-h-60 overflow-y-auto">
                  {students.map((student) => (
                    <div
                      key={student.id}
                      onClick={() => selectStudent(student)}
                      className="p-3 hover:bg-blue-50 cursor-pointer"
                    >
                      <p className="font-medium">{student.full_name}</p>
                      <p className="text-sm text-gray-500">
                        {student.admission_number} | {student.course_name}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {/* Selected Student Info */}
              {selectedStudent && studentStatus && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="font-semibold text-lg">{selectedStudent.full_name}</p>
                      <p className="text-sm text-gray-600">
                        {selectedStudent.admission_number} | {selectedStudent.course_name}
                      </p>
                    </div>
                    <Button variant="outline" size="sm" onClick={resetForm}>Change</Button>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="p-2 bg-white rounded">
                      <p className="text-gray-500">Required</p>
                      <p className="font-semibold text-lg">₹{studentStatus.required_amount?.toLocaleString('en-IN')}</p>
                    </div>
                    <div className="p-2 bg-white rounded">
                      <p className="text-gray-500">Paid</p>
                      <p className="font-semibold text-lg text-green-600">₹{studentStatus.total_paid?.toLocaleString('en-IN')}</p>
                    </div>
                    <div className="p-2 bg-white rounded">
                      <p className="text-gray-500">Balance</p>
                      <p className="font-semibold text-lg text-amber-600">
                        ₹{(studentStatus.required_amount - studentStatus.total_paid)?.toLocaleString('en-IN')}
                      </p>
                    </div>
                    <div className="p-2 bg-white rounded">
                      <p className="text-gray-500">Status</p>
                      <p className={`font-semibold ${
                        studentStatus.status === 'Fully Paid' ? 'text-green-600' :
                        studentStatus.status === 'Partially Paid' ? 'text-amber-600' : 'text-red-600'
                      }`}>
                        {studentStatus.status}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Right - Payment Form */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Payment Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePayment} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date</label>
                  <input type="date" value={paymentData.payment_date} onChange={(e) => setPaymentData(prev => ({ ...prev, payment_date: e.target.value }))} max={new Date().toISOString().split('T')[0]} className="w-full px-3 py-2 border border-gray-300 rounded-lg" data-testid="caution-payment-date" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount (₹)</label>
                  <input
                    type="number"
                    value={paymentData.amount}
                    onChange={(e) => setPaymentData(prev => ({ ...prev, amount: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-lg font-semibold"
                    placeholder="Enter amount"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Mode</label>
                  <select
                    value={paymentData.payment_mode}
                    onChange={(e) => setPaymentData(prev => ({ ...prev, payment_mode: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="Cash">Cash</option>
                    <option value="Bank Transfer">Bank Transfer</option>
                    <option value="UPI">UPI</option>
                    <option value="Cheque">Cheque</option>
                    <option value="Card">Card</option>
                  </select>
                </div>

                {paymentData.payment_mode !== 'Cash' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Select Bank</label>
                      <select
                        value={paymentData.bank_id}
                        onChange={(e) => {
                          const bank = banks.find(b => b.id === e.target.value);
                          setPaymentData(prev => ({
                            ...prev,
                            bank_id: e.target.value,
                            bank_name: bank?.bank_name || ''
                          }));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        required
                      >
                        <option value="">Select Bank</option>
                        {banks.map((bank) => (
                          <option key={bank.id} value={bank.id}>{bank.bank_name} - {bank.account_number}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Transaction Reference</label>
                      <input
                        type="text"
                        value={paymentData.transaction_ref}
                        onChange={(e) => setPaymentData(prev => ({ ...prev, transaction_ref: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="UTR / Cheque No / Reference"
                      />
                    </div>
                  </>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Remarks (Optional)</label>
                  <textarea
                    value={paymentData.remarks}
                    onChange={(e) => setPaymentData(prev => ({ ...prev, remarks: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    rows="2"
                    placeholder="Any additional notes..."
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-green-600 hover:bg-green-700"
                  disabled={loading || !selectedStudent}
                >
                  {loading ? 'Processing...' : 'Collect Caution Money'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      ) : (
        /* Receipt with Tenant Branding */
        <Card className="max-w-2xl mx-auto print:shadow-none" id="receipt-content">
          <CardContent className="p-8">
            {/* Tenant Header */}
            <div className="text-center mb-6 border-b pb-4">
              {receipt.tenant?.logo_url && (
                <img src={receipt.tenant.logo_url} alt="Logo" className="h-16 mx-auto mb-2" />
              )}
              <h1 className="text-xl font-bold text-gray-900">{receipt.tenant?.college_name || receipt.tenant?.name || 'Institution Name'}</h1>
              <p className="text-sm text-gray-600">{receipt.tenant?.address || ''}</p>
              <p className="text-sm text-gray-600">
                {receipt.tenant?.phone && `Phone: ${receipt.tenant.phone}`}
                {receipt.tenant?.phone && receipt.tenant?.email && ' | '}
                {receipt.tenant?.email && `Email: ${receipt.tenant.email}`}
              </p>
              {receipt.tenant?.website && (
                <p className="text-sm text-blue-600">{receipt.tenant.website}</p>
              )}
            </div>

            <div className="text-center mb-4">
              <h2 className="text-xl font-bold border-2 border-gray-800 inline-block px-6 py-2">CAUTION MONEY RECEIPT</h2>
              <p className="text-gray-600 text-sm mt-1">Security Deposit</p>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <p className="text-sm text-gray-500">Receipt No</p>
                <p className="font-semibold">{receipt.receipt_no}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500">Date</p>
                <p className="font-semibold">{receipt.date}</p>
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <p className="text-sm text-gray-500">Received From</p>
              <p className="font-semibold text-lg">{receipt.student?.full_name}</p>
              <p className="text-gray-600">{receipt.student?.admission_number} | {receipt.student?.course_name}</p>
            </div>

            <div className="border-2 border-green-500 rounded-lg p-4 mb-6 text-center bg-green-50">
              <p className="text-sm text-gray-500">Amount Received</p>
              <p className="text-3xl font-bold text-green-600">₹{receipt.amount?.toLocaleString('en-IN')}</p>
              <p className="text-sm text-gray-500 mt-1">Payment Mode: {receipt.payment_mode}</p>
            </div>

            <div className="text-sm text-gray-600 mb-6">
              <p><strong>Note:</strong> This is a refundable security deposit. It will be refunded upon course completion subject to clearance of all dues and no damage to institute property.</p>
            </div>

            <div className="flex justify-between items-end pt-4 border-t">
              <div>
                <p className="text-sm text-gray-500">Received By</p>
                <p className="font-medium">{localStorage.getItem('userName') || 'Admin'}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500">Authorized Signature</p>
                <div className="w-32 border-b border-gray-400 mt-8"></div>
              </div>
            </div>

            <div className="flex gap-3 mt-6 print:hidden">
              <Button onClick={printReceipt} className="flex-1">
                <Printer className="h-4 w-4 mr-2" /> Print Receipt
              </Button>
              <Button variant="outline" onClick={resetForm} className="flex-1">
                New Payment
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
